import type {ChallengeResult, Sealed} from "../types";
import {doProofOfWork} from "./work";
import {debug, info} from "../utils/log";
import {apiFetch} from "../utils/api";
import {API_PREFIX} from "../utils/constants";
import {getSignKeyPublic, rotateSignKeys, signDataInline} from "../utils/crypto";
import {TranslatableError} from "../utils/error";

const encodeHex = (arr: Uint8Array) => {
    let result = '';
    for (let i = 0; i < arr.length; i++) {
        const hex = arr[i].toString(16).padStart(2, '0');
        result += hex;
    }
    return result
}

export async function getNewSolution(update: (rate: number) => void, signal: AbortSignal): Promise<string | undefined> {
    // We need a new sign key to get a new challenge
    await rotateSignKeys();
    let publicSignKey = await getSignKeyPublic();
    let lastChallenge: string | undefined = undefined;
    let lastNonce: string | undefined = undefined;
    const start = Date.now();
    while (true) {
        const challenge: ChallengeResult = await apiFetch<ChallengeResult>(API_PREFIX + "pow/challenge", {
            body: {
                publicSignKey,
                lastChallenge,
                lastNonce,
            },
            signal: signal,
        });
        if (challenge.rejected) {
            if (challenge.rejected === "key-reuse" ) {
                info(`Key reuse detected, rotating keys and start over...`);
                await rotateSignKeys();
                publicSignKey = await getSignKeyPublic();
                lastChallenge = undefined;
                lastNonce = undefined;
                continue;
            }
            throw new TranslatableError({id: 'rejected'})
        }
        const t0 = Date.now();
        let lastSpeedUpdate = t0;
        let lastIter = 0;
        const {nonce, result} = await doProofOfWork(
            challenge,
            "0",
            (currentNonce) => {
                const currentNonceNumber = Number(currentNonce);
                const n = Date.now()
                const delta = n - lastSpeedUpdate;
                const rate = delta === 0 ? 0 : (currentNonceNumber - lastIter) / delta;
                update(rate)
                lastSpeedUpdate = n;
                lastIter = currentNonceNumber;
            },
            signal,
        );
        const t1 = Date.now();
        const elapsed = t1 - t0;
        debug(`New solution computed: ${nonce} in ${elapsed}ms`, {
            challenge,
            nonce,
            result,
            elapsed,
            resultHex: encodeHex(result)
        });
        lastChallenge = challenge.challenge;
        lastNonce = nonce.toString();
        const response = await apiFetch<Sealed>(API_PREFIX + "pow/submit", {
            body: {
                challenge: challenge.challenge,
                nonce: await signDataInline(nonce.toString()),
                elapsed,
                publicSignKey,
            },
            signal: signal,
        });
        if (response.solution) {
            const solution = JSON.stringify(response.solution);
            const elapsed = Date.now() - start;
            info(`Solution computed and the server successfully attested it. This took ${elapsed}ms from start to finish.`);
            return solution;
        } else {
            debug(`Server wants us to compute another solution, continuing...`);
        }
    }
}

// TODO: Move this to a better place

const OLD_KEY_PREFIX = "saasweb-spam-shield-v1-";
const removeItem = localStorage.removeItem.bind(localStorage);
function cleanupStorage(): void {
    let oldKeys: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(OLD_KEY_PREFIX)) {
            oldKeys.push(key);
        }
    }
    oldKeys.forEach(removeItem)
}
cleanupStorage()
